<template>
  <q-dialog v-model="isOpen" persistent maximized transition-show="slide-up" transition-hide="slide-down">
    <q-card class="video-generator-dialog">
      <VideoGeneratorTopBar :image="image" :hasImage="hasImage" @update:isOpen="$emit('update:isOpen', $event)" />
      <div class="generator-container">
        <!-- Left Section - Image Preview and Settings -->
        <div class="left-section">
          <!-- AI Video Generator Header -->
          <div class="section-header">
            <h2 class="section-title">AI Video Generator</h2>
            <p class="section-description">Transform your images into stunning videos with AI</p>
          </div>

          <!-- Upload Area (when no image preview is available) -->
          <div v-if="!hasImagePreview" class="upload-section">
            <div class="upload-area" :class="{ 'drag-over': isDragging }" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop" @click="triggerFileInput">
              <input type="file" ref="fileInput" @change="onFileSelected" accept="image/jpeg,image/jpg,image/webp" class="hidden-input" />

              <div class="upload-placeholder">
                <div class="upload-icon-container">
                  <q-icon name="cloud_upload" size="30px" color="grey-5" />
                </div>
                <div class="upload-text">
                  <h3 class="upload-main-text">Drop your image here</h3>
                  <p class="upload-sub-text">or click to browse files</p>
                </div>
                <div class="upload-formats">Supported formats: JPEG, WebP</div>
              </div>
            </div>
          </div>

          <!-- Image Preview (when image is available) -->
          <div v-else>
            <div class="image-preview-section">
              <div class="selected-image-container">
                <div class="image-preview-wrapper">
                  <q-img
                    :src="currentImage.optimized_url || currentImage.url || currentImage.preview"
                    :placeholder-src="currentImage.preview_url"
                    class="selected-image"
                    fit="contain"
                    @load="handleImageLoad"
                    @error="handleImageError"
                  />
                  <div v-if="!currentImage.preview_url" class="image-overlay">
                    <q-btn round flat dense icon="close" color="white" class="remove-btn" @click.stop="handleImageRemoved" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Upload Button Section (below image preview when not uploaded) -->
            <div v-if="uploadedImage && !uploadedImage.isUploaded" class="upload-action-section">
              <q-btn class="upload-action-button" color="primary" @click="uploadSelectedImage" :loading="$store.getters['uploadProgress/hasActiveUploads']" no-caps unelevated>
                <template v-if="!$store.getters['uploadProgress/hasActiveUploads']">
                  <q-icon name="cloud_upload" size="16px" class="q-mr-sm" />
                  Upload Image
                </template>
                <template v-slot:loading>
                  <q-spinner color="white" class="on-left" />
                  Uploading...
                </template>
              </q-btn>
            </div>
          </div>

          <!-- Camera Movement Selection -->
          <div v-if="hasImage" class="camera-movement-section">
            <div class="movement-header">
              <label class="movement-caption">Choose a camera effect below:</label>
            </div>

            <!-- Camera Movement Grid -->
            <div class="movement-grid">
              <div
                v-for="movement in klingMovements"
                :key="movement.value"
                class="movement-card"
                :class="{ 'movement-card--selected': selectedLabel === movement.value, 'movement-card--disabled': isSelectionDisabled }"
                @click="!isSelectionDisabled && selectMovement(movement.value)"
                @mouseenter="showMovementTooltip(movement.value, $event)"
                @mouseleave="hideMovementTooltip"
              >
                <div class="movement-icon">
                  <q-icon :name="movement.icon" size="24px" />
                </div>
                <div class="movement-label">{{ movement.label }}</div>
              </div>
            </div>
          </div>

          <!-- Helper Text for Generate Button -->
          <div class="helper-text-container">
            <div v-if="!hasImagePreview" class="helper-text helper-text--warning">
              <div class="helper-text-content">
                <q-icon name="upload" size="18px" class="helper-icon" />
                <span class="helper-message">Upload an image to get started</span>
              </div>
            </div>
            <div v-else-if="hasImagePreview && !hasImage" class="helper-text helper-text--warning">
              <div class="helper-text-content">
                <q-icon name="cloud_upload" size="18px" class="helper-icon" />
                <span class="helper-message">Click upload button to continue</span>
              </div>
            </div>
            <div v-else-if="isSelectionDisabled && !isGenerating" class="helper-text helper-text--info">
              <div class="helper-text-content">
                <q-icon name="hourglass_empty" size="18px" class="helper-icon" />
                <span class="helper-message">Please wait for current video to complete</span>
              </div>
            </div>
            <div v-else-if="!selectedLabel" class="helper-text helper-text--warning">
              <div class="helper-text-content">
                <q-icon name="videocam" size="18px" class="helper-icon" />
                <span class="helper-message">Choose a camera movement</span>
              </div>
            </div>
            <div v-else class="helper-text helper-text--success">
              <div class="helper-text-content">
                <q-icon name="check_circle" size="18px" class="helper-icon" />
                <span class="helper-message">Ready to generate your video!</span>
              </div>
            </div>
          </div>

          <!-- Generate Button -->
          <div v-if="hasImage" class="action-section">
            <q-btn class="action-button action-button--primary" :loading="isGenerating" :disable="isSelectionDisabled || !hasImage || !selectedLabel" @click="generateVideo" flat no-caps>
              <template v-if="!isGenerating">
                <q-icon name="movie" size="xs" class="button-icon" color="grey-7" />
                Generate Video
              </template>
              <template v-slot:loading>
                <q-spinner color="grey-7" class="on-left" />
                <span v-if="operationStatus === 'preparing'">AI At Work… <q-icon name="fas fa-magic" size="xs" color="grey-7" /> </span>
                <span v-else-if="operationStatus === 'starting'">Working Our Magic! <q-icon name="fas fa-hat-wizard" size="xs" color="grey-7" /> </span>
                <span v-else-if="operationStatus === 'processing'">Almost Ready... <q-icon name="fas fa-hourglass-half" size="xs" color="grey-7" /></span>
              </template>
            </q-btn>
          </div>
        </div>

        <!-- Right Section - Generated Videos Grid -->
        <div class="right-section">
          <!-- Generated Videos Header -->
          <div class="section-header-generator">
            <h2 class="section-title">Generated Videos</h2>
            <div class="badge">AI Generated</div>
          </div>

          <!-- Videos Grid -->
          <div class="videos-grid">
            <!-- Processing Placeholder - When Generating a Video or when there are processing items -->
            <div v-if="hasAnyProcessingItem" class="video-item processing-item">
              <div class="video-thumbnail-container processing-thumbnail">
                <div class="processing-indicator">
                  <div class="progress-container">
                    <!-- Show simple spinner when overtime, otherwise show progress -->
                    <template v-if="isOvertime">
                      <q-spinner color="primary" size="60px" />
                    </template>
                    <template v-else>
                      <!-- Circular Progress Indicator -->
                      <q-circular-progress
                        show-value
                        :value="currentProcessingItem ? currentProcessingItem.progress : 0"
                        size="60px"
                        :thickness="0.12"
                        color="primary"
                        track-color="grey-3"
                        class="progress-circle"
                      >
                        <span class="progress-percentage">{{ Math.round(currentProcessingItem ? currentProcessingItem.progress : 0) }}%</span>
                      </q-circular-progress>
                    </template>
                  </div>
                  <div class="processing-text">
                    {{ isOvertime ? 'Taking longer than expected...' : currentProcessingItem && currentProcessingItem.isLocal ? getProcessingText : 'Generating video...' }}
                  </div>
                  <!-- Time Remaining Display - hide when overtime -->
                  <div v-if="!isOvertime && currentProcessingItem && currentProcessingItem.timeRemaining" class="time-remaining">
                    {{ currentProcessingItem.timeRemaining }}
                  </div>
                </div>
              </div>
              <div class="video-info">
                <div class="video-details">
                  <h5 class="video-title">AI Generated Video</h5>
                  <div class="video-type">{{ currentProcessingItem ? currentProcessingItem.cameraEffect : selectedLabel }}</div>
                </div>
                <div class="video-actions">
                  <q-btn flat round size="sm" icon="hourglass_empty" disabled />
                  <q-btn flat round size="sm" icon="more_horiz" disabled />
                </div>
              </div>
            </div>

            <!-- Recent Video History Items -->
            <div v-for="(item, index) in videoHistory" :key="`history-${item.id || index}`" class="video-item">
              <div class="video-thumbnail-container" @click="playVideo(item, $event)">
                <!-- Video or placeholder -->
                <video v-if="item.url" class="video-thumbnail" :src="item.url" preload="metadata" @error="handleVideoError($event, item)" @loadedmetadata="handleVideoLoaded($event, item)"></video>
                <div v-else-if="!item.url" class="video-placeholder-thumbnail">
                  <q-icon name="videocam_off" size="24px" color="grey-5" />
                  <div class="placeholder-text">{{ item.status === 'failed' ? 'Failed' : 'No video' }}</div>
                </div>

                <div class="duration">6s</div>

                <!-- Error state overlay -->
                <div v-if="item.hasError" class="video-error-overlay">
                  <q-icon name="error" size="24px" color="red" />
                  <div class="error-text">Failed to load</div>
                </div>
              </div>
              <div class="video-info">
                <div class="video-details">
                  <h5 class="video-title">AI Generated Video</h5>
                  <div class="video-type">{{ item.label }}</div>
                </div>
                <div class="video-actions">
                  <q-btn flat round size="sm" icon="play_arrow" @click="playVideo(item, $event)" :disable="item.hasError" />
                  <q-btn flat round size="sm" icon="file_download" @click="downloadVideo(item.url)" :disable="item.hasError" />
                </div>
              </div>
            </div>

            <!-- Create New Video Placeholder (always shown when not generating and has content) -->
            <div v-if="!isGenerating && (videoHistory.length > 0 || hasImage)" class="video-placeholder create-placeholder" @click="createNewClickHandler">
              <div class="placeholder-content">
                <div class="placeholder-icon-container">
                  <q-icon name="add" size="24px" />
                </div>
                <div class="placeholder-text">Create new video</div>
              </div>
            </div>

            <!-- Fill remaining grid slots with empty placeholders -->
            <template v-if="totalGridItems < 9">
              <div v-for="n in 9 - totalGridItems" :key="`placeholder-${n}`" class="video-placeholder"></div>
            </template>

            <!-- Empty State - Show when no content at all -->
            <template v-if="!hasImagePreview && !isGenerating && videoHistory.length === 0">
              <!-- Empty placeholders -->
              <div v-for="n in 9" :key="`empty-placeholder-${n}`" class="video-placeholder"></div>
            </template>
          </div>
        </div>
      </div>

      <!-- Video Tooltip -->
      <VideoTooltip :movement-id="hoveredMovement" :name="hoveredMovement" :description="getMovementDescription(hoveredMovement)" :position-style="tooltipStyles" />
    </q-card>
  </q-dialog>
</template>

<script>
import { ReplicateApi } from '@api/index';
import VideoGeneratorTopBar from '@components/video-generator/VideoGeneratorTopBar.vue';
import VideoTooltip from '@components/video-generator/VideoTooltip.vue';
import { useQueryClient } from '@tanstack/vue-query';
import fileUpload from '@/utils/fileUpload';
import helpers from '@/utils/helpers';
import { createProgressCallback } from '@/utils/uploadHelpers';
import axios from 'axios';

export default {
  name: 'VideoGenerator',
  components: {
    VideoGeneratorTopBar,
    VideoTooltip
  },
  props: {
    image: {
      type: Object,
      required: false,
      default: null
    },
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  setup() {
    // Initialize TanStack Query client for Vue 2 compatibility
    const queryClient = useQueryClient();
    return { queryClient };
  },
  data() {
    return {
      selectedLabel: '',
      isGenerating: false,
      operationStatus: null,
      operationMessage: null,
      originalImageUrl: null,
      videoUrl: null,
      isActiveVideo: false,
      isPlaying: false,
      videoDuration: '0:6',
      uploadedImage: null,
      isDragging: false,
      // Progress indicator properties
      generationProgress: 0,
      progressTimer: null,
      progressStartTime: null,
      progressDuration: 240000, // 4 minutes in milliseconds
      isCompletingProgress: false, // Flag to track smooth completion animation
      pendingVideoUrl: null, // Store video URL during progress completion
      currentPredictionId: null, // Track current prediction ID for syncing with global store
      // Event handler references for proper cleanup
      videoEventHandlers: {
        play: null,
        pause: null,
        ended: null
      },
      // Kling camera movements with icons
      klingMovements: [
        { label: 'Turntable 360', value: 'Turntable 360', icon: 'refresh' },
        { label: 'Hero Focus', value: 'Hero Focus', icon: 'center_focus_strong' },
        { label: 'Smooth Entry', value: 'Smooth Entry', icon: 'trending_up' },
        { label: 'Gimbal Glide', value: 'Gimbal Glide', icon: 'photo_camera' },
        { label: 'Portrait Pan', value: 'Portrait Pan', icon: 'person' },
        { label: 'Real Look', value: 'Real Look', icon: 'visibility' }
      ],
      // Tooltip functionality
      hoveredMovement: null,
      tooltipStyles: {
        top: '0px',
        left: '0px'
      }
    };
  },
  mounted() {
    if (this.currentImage && this.currentImage.optimized_url) {
      this.originalImageUrl = this.currentImage.optimized_url; // keep track of the original image url
    }
    // Add event listener for Escape key
    document.addEventListener('keydown', this.handleKeydown);

    // Load video history from API if we have an image
    // Use nextTick to ensure component is fully mounted
    this.$nextTick(() => {
      if (this.hasImage) {
        this.loadVideoHistoryFromApi();
      }
    });
  },
  beforeDestroy() {
    // Remove event listener for cleanup
    document.removeEventListener('keydown', this.handleKeydown);

    // Clean up video player event listeners and pause any playing videos
    if (this.$refs.videoPlayer) {
      const videoPlayer = this.$refs.videoPlayer;

      // Remove event listeners using the stored references
      if (this.videoEventHandlers.play) {
        videoPlayer.removeEventListener('play', this.videoEventHandlers.play);
      }

      if (this.videoEventHandlers.pause) {
        videoPlayer.removeEventListener('pause', this.videoEventHandlers.pause);
      }

      if (this.videoEventHandlers.ended) {
        videoPlayer.removeEventListener('ended', this.videoEventHandlers.ended);
      }

      // Reset event handler references
      this.videoEventHandlers = {
        play: null,
        pause: null,
        ended: null
      };

      // Pause the video
      videoPlayer.pause();
    }

    // Clear the generateVideo query parameter while preserving other query parameters
    const query = { ...this.$router.currentRoute.query };
    if (query.generateVideo) {
      delete query.generateVideo;
      this.$router.replace({ query }).catch(err => {
        // Only log errors that aren't navigation duplications
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error during cleanup:', err);
        }
      });
    }

    // Clean up progress timer
    this.stopProgressTimer();

    // Hand off active generation tracking to the global store
    if (this.currentPredictionId && this.isGenerating) {
      // Mark as no longer actively managed so global store can take over
      this.$store.dispatch('videoGenerationProgress/updateProgressForGeneration', {
        id: this.currentPredictionId,
        progress: this.generationProgress,
        elapsed: Date.now() - this.progressStartTime,
        isActivelyManaged: false // Hand off to store
      });

      // Ensure global progress timer starts to continue tracking
      this.$store.dispatch('videoGenerationProgress/ensureProgressTimer');
    }

    // Clean up uploaded image URL if it exists
    if (this.uploadedImage && this.uploadedImage.url) {
      URL.revokeObjectURL(this.uploadedImage.url);
    }

    // Clean up video history
    this.$store.dispatch('videoHistory/clearVideoHistory');

    // Reset all data properties to their initial state
    this.selectedLabel = '';
    this.isGenerating = false;
    this.operationStatus = null;
    this.operationMessage = null;
    this.videoUrl = null;
    this.isActiveVideo = false;
    this.isPlaying = false;
    this.generationProgress = 0;
    this.isCompletingProgress = false;
    this.pendingVideoUrl = null;
    this.currentPredictionId = null; // Clear prediction ID reference
    this.uploadedImage = null; // Reset uploaded image
  },
  computed: {
    getStatusText() {
      const statusMap = {
        starting: 'Starting video generation',
        processing: 'Generating video... This may take a minute',
        saving: 'Finalizing video',
        error: 'An error occurred'
      };

      if (!this.operationStatus) return 'Preparing...';

      return statusMap[this.operationStatus] || 'Processing...';
    },

    // Calculate time remaining based on current progress
    timeRemaining() {
      if (!this.isGenerating || !this.progressStartTime || this.generationProgress >= 100) {
        return null;
      }

      const elapsed = Date.now() - this.progressStartTime;
      const progressDecimal = this.generationProgress / 100;

      // Avoid division by zero and handle very small progress values
      if (progressDecimal <= 0.01) {
        // For very early progress, show the full duration minus elapsed time
        return Math.max(0, this.progressDuration - elapsed);
      }

      // For progress near completion (95%+), show a small remaining time
      if (progressDecimal >= 0.95) {
        return Math.min(30000, Math.max(5000, this.progressDuration - elapsed)); // 5-30 seconds
      }

      // Calculate estimated total time based on current progress
      const estimatedTotalTime = elapsed / progressDecimal;
      const remainingTime = estimatedTotalTime - elapsed;

      // Handle edge cases and apply reasonable bounds
      if (remainingTime <= 0) {
        return 5000; // Show at least 5 seconds remaining
      }

      // Cap the maximum remaining time to avoid unrealistic estimates
      const maxRemainingTime = this.progressDuration - elapsed + 30000; // Add 30s buffer
      return Math.min(remainingTime, maxRemainingTime);
    },

    // Format time remaining in user-friendly format
    formattedTimeRemaining() {
      const remaining = this.timeRemaining;

      if (remaining === null || remaining <= 0) {
        return null;
      }

      const totalSeconds = Math.ceil(remaining / 1000);

      // Show "Almost done..." for very short remaining times
      if (totalSeconds <= 10) {
        return 'Almost done...';
      }

      if (totalSeconds < 60) {
        return `${totalSeconds}s remaining`;
      }

      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      if (minutes < 60) {
        return seconds > 0 ? `${minutes}m ${seconds}s remaining` : `${minutes}m remaining`;
      }

      // For times over an hour (shouldn't happen with 3min duration, but just in case)
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m remaining`;
    },

    // Get appropriate processing text based on current state
    getProcessingText() {
      if (this.isCompletingProgress) {
        return 'Finalizing video...';
      }
      return 'Generating video...';
    },

    // Get the current image to use (from props or uploaded)
    currentImage() {
      return this.uploadedImage || this.image;
    },

    // Check if we have an image to work with (for video generation - needs to be uploaded)
    hasImage() {
      return !!(this.currentImage && (this.currentImage.url || this.currentImage.optimized_url) && (this.currentImage.id || this.currentImage.content_id));
    },

    // Check if we have an image preview (for display - can be local preview or uploaded)
    hasImagePreview() {
      return !!(this.currentImage && (this.currentImage.url || this.currentImage.optimized_url));
    },

    // Get video history from store (one-time load, no polling)
    videoHistory() {
      // If no uploaded image (just preview), return empty array
      if (!this.hasImage) {
        return [];
      }

      // Get data from store
      const historyData = this.$store.getters['videoHistory/getVideoHistoryData'];

      if (!historyData || !historyData.histories) {
        return [];
      }

      // Transform API response - show completed items, processing items are handled by main placeholder
      return historyData.histories
        .map(history => ({
          id: history.job_id, // Use job_id as the unique identifier (replicate prediction ID)
          historyId: history.id, // Keep the database ID for reference
          url: history.result, // Video URL
          label: history.camera_effect || 'AI Generated', // Camera effect from backend
          timestamp: history.created_at,
          status: history.job_status,
          content_id: history.content_id,
          content_type: history.content_type,
          model: history.model,
          predictionId: history.job_id, // Store prediction ID for comparison with current generation
          hasError: history.job_status === 'failed' || history.job_status === 'error' || (!history.result && history.job_status === 'succeeded'),
          isProcessing: history.job_status === 'processing' || history.job_status === 'starting'
        }))
        .filter(item => {
          // Hide processing items from regular history - they're shown in main processing placeholder
          return !(item.status === 'processing' || item.status === 'starting');
        });
    },

    totalGridItems() {
      let count = this.videoHistory.length;

      // Add processing item if generating
      if (this.isGenerating || this.isCompletingProgress) {
        count += 1;
      }

      // Add create new placeholder if it should be shown
      if (!this.isGenerating && (this.videoHistory.length > 0 || this.hasImage)) {
        count += 1;
      }

      return count;
    },

    // Check if there's any processing item (local generation or from API)
    hasAnyProcessingItem() {
      // Local generation in progress (including completion animation)
      if (this.isGenerating || this.isCompletingProgress) {
        return true;
      }

      // Check raw API data for processing items (not filtered videoHistory)
      const historyData = this.$store.getters['videoHistory/getVideoHistoryData'];
      if (historyData && historyData.histories) {
        return historyData.histories.some(history => history.job_status === 'processing' || history.job_status === 'starting');
      }

      return false;
    },

    // Get the current processing item (for the main processing placeholder)
    currentProcessingItem() {
      // If locally generating or completing, use local data
      if (this.isGenerating || this.isCompletingProgress) {
        return {
          progress: this.isOvertime ? 100 : this.generationProgress, // Show 100% when overtime but use spinner instead of percentage
          cameraEffect: this.selectedLabel,
          timeRemaining: this.isCompletingProgress ? 'Finalizing...' : this.isOvertime ? null : this.formattedTimeRemaining,
          isLocal: true
        };
      }

      // Otherwise, find processing item from raw API data and get its data from global store
      const historyData = this.$store.getters['videoHistory/getVideoHistoryData'];
      if (historyData && historyData.histories) {
        const processingHistoryItem = historyData.histories.find(history => history.job_status === 'processing' || history.job_status === 'starting');

        if (processingHistoryItem) {
          const activeGenerations = this.$store.getters['videoGenerationProgress/activeGenerations'] || [];
          const generation = activeGenerations.find(gen => gen.predictionId === processingHistoryItem.job_id);

          if (generation) {
            return {
              progress: generation.progress || 0,
              cameraEffect: processingHistoryItem.camera_effect || 'AI Generated',
              timeRemaining: this.getFormattedTimeRemaining(generation),
              isLocal: false
            };
          }
        }
      }

      return null;
    },

    // Disable camera movement selection when there are processing items for this content
    isSelectionDisabled() {
      // Check if there are any processing items in the raw API data for this content
      const historyData = this.$store.getters['videoHistory/getVideoHistoryData'];
      if (historyData && historyData.histories) {
        return historyData.histories.some(history => history.job_status === 'processing' || history.job_status === 'starting');
      }

      // Also check if locally generating
      return this.isGenerating || this.isCompletingProgress;
    },

    // Check if we've exceeded the estimated time and should show simple loading
    isOvertime() {
      if (!this.isGenerating || !this.progressStartTime || this.isCompletingProgress) {
        return false;
      }
      const elapsed = Date.now() - this.progressStartTime;
      return elapsed >= this.progressDuration;
    }
  },

  watch: {
    // Watch for route changes to ensure proper handoff to global store
    $route(to, from) {
      // If we're navigating away while generating, hand off to global store
      if (this.currentPredictionId && this.isGenerating && to.path !== from.path) {
        // Mark as no longer actively managed so global store can take over
        this.$store.dispatch('videoGenerationProgress/updateProgressForGeneration', {
          id: this.currentPredictionId,
          progress: this.generationProgress,
          elapsed: this.progressStartTime ? Date.now() - this.progressStartTime : 0,
          isActivelyManaged: false // Hand off to store
        });

        // Ensure global progress timer starts to continue tracking
        this.$store.dispatch('videoGenerationProgress/ensureProgressTimer');
      }
    },

    // Watch for image changes to reload video history
    currentImage: {
      handler(newImage, oldImage) {
        // Only reload if we have a new image with an ID
        if (newImage && newImage.id && (!oldImage || newImage.id !== oldImage.id)) {
          this.loadVideoHistoryFromApi();
        }
      },
      immediate: false
    },

    // Watch for dialog opening to reload history if needed
    isOpen: {
      handler(newIsOpen, oldIsOpen) {
        // When dialog opens and we have an image but no history data, reload
        if (newIsOpen && !oldIsOpen && this.hasImage) {
          const historyData = this.$store.getters['videoHistory/getVideoHistoryData'];
          if (!historyData) {
            this.loadVideoHistoryFromApi();
          }
        }
      },
      immediate: false
    }
  },

  methods: {
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('update:isOpen', false);
      }
    },

    createNewClickHandler() {
      // If we have values selected, generate the video
      if (this.selectedLabel) {
        this.generateVideo();
      }
    },

    selectMovement(movementValue) {
      // Toggle selection - if clicking the same movement, deselect it
      if (this.selectedLabel === movementValue) {
        this.selectedLabel = '';
      } else {
        this.selectedLabel = movementValue;
      }
    },

    formatContentType(type) {
      // Keep for backward compatibility with video history
      const typeMap = {
        ecommerce: 'E-commerce',
        interiors: 'Interiors',
        fnb: 'Food & Beverage',
        headshots: 'Headshots'
      };
      return typeMap[type] || type;
    },

    // Progress timer management methods
    startProgressTimer() {
      // Reset progress state
      this.generationProgress = 0;
      this.progressStartTime = Date.now();
      this.isCompletingProgress = false;

      // Clear any existing timer
      this.stopProgressTimer();

      // Start the progress timer
      this.progressTimer = setInterval(() => {
        this.updateProgress();
      }, 100); // Update every 100ms for smooth animation
    },

    updateProgress() {
      if (!this.progressStartTime || this.isCompletingProgress) return;

      const elapsed = Date.now() - this.progressStartTime;
      let progress = (elapsed / this.progressDuration) * 100;

      // If we exceed the estimated time, stop the progress timer and show simple loading
      if (elapsed >= this.progressDuration) {
        this.generationProgress = 100; // Set to 100% but with different UI state
        this.stopProgressTimer();
        return;
      }

      // Normal progress calculation - keep it simple
      progress = Math.min(progress, 95); // Cap at 95% until actual completion
      this.generationProgress = progress;

      // Sync progress to global store using the centralized method
      if (this.currentPredictionId) {
        this.$store.dispatch('videoGenerationProgress/updateProgressForGeneration', {
          id: this.currentPredictionId,
          progress: progress,
          elapsed: elapsed,
          isActivelyManaged: true // Mark as actively managed by VideoGenerator
        });
      }
    },

    stopProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
    },

    completeProgress(onComplete = null) {
      // Smoothly animate from current progress to 100% when generation completes
      if (this.isCompletingProgress) return; // Prevent multiple calls

      this.isCompletingProgress = true;
      this.stopProgressTimer(); // Stop the main timer

      // If we were in overtime, start from 95% for smooth completion animation
      const startProgress = this.isOvertime ? 95 : this.generationProgress;
      const targetProgress = 100;
      const animationDuration = this.isOvertime ? 800 : 1500; // Shorter animation if coming from overtime
      const startTime = Date.now();

      const animateCompletion = () => {
        const elapsed = Date.now() - startTime;
        const animationProgress = Math.min(elapsed / animationDuration, 1);

        // Use easeOut animation for smooth deceleration
        const easeOut = 1 - Math.pow(1 - animationProgress, 3);
        const currentProgress = startProgress + (targetProgress - startProgress) * easeOut;

        this.generationProgress = currentProgress;

        // Sync with global store during completion animation
        if (this.currentPredictionId) {
          this.$store.dispatch('videoGenerationProgress/updateProgressForGeneration', {
            id: this.currentPredictionId,
            progress: currentProgress,
            elapsed: this.progressStartTime ? Date.now() - this.progressStartTime : 0,
            isActivelyManaged: true
          });
        }

        if (animationProgress < 1) {
          requestAnimationFrame(animateCompletion);
        } else {
          this.generationProgress = 100;

          // Keep the progress at 100% for a moment before transitioning
          setTimeout(() => {
            this.isCompletingProgress = false;
            this.isGenerating = false; // Mark generation as complete

            // Execute callback after animation completes
            if (onComplete && typeof onComplete === 'function') {
              onComplete();
            }
          }, 500); // Hold at 100% for half a second
        }
      };

      requestAnimationFrame(animateCompletion);
    },

    // Handle displaying the video after progress completion
    displayCompletedVideo() {
      if (!this.pendingVideoUrl) return;

      // Set the video URL to display it in the UI
      this.videoUrl = this.pendingVideoUrl;
      this.pendingVideoUrl = null;

      // Set final status
      this.isGenerating = false;
      this.operationStatus = 'success';
      this.operationMessage = 'Video generated successfully!';

      // Attempt to play the video
      this.$nextTick(() => {
        if (this.$refs.videoPlayer) {
          const videoPlayer = this.$refs.videoPlayer;

          // Define event handlers and store references for cleanup
          this.videoEventHandlers.play = () => {
            this.isPlaying = true;
          };

          this.videoEventHandlers.pause = () => {
            this.isPlaying = false;
          };

          this.videoEventHandlers.ended = () => {
            this.isPlaying = false;
            // Auto-replay when video ends
            setTimeout(() => {
              videoPlayer.play().catch(err => console.error('Error replaying video:', err));
            }, 500);
          };

          // Add event listeners using the stored references
          videoPlayer.addEventListener('play', this.videoEventHandlers.play);
          videoPlayer.addEventListener('pause', this.videoEventHandlers.pause);
          videoPlayer.addEventListener('ended', this.videoEventHandlers.ended);

          videoPlayer.load();
          videoPlayer.play().catch(err => console.error('Error playing video:', err));
          this.isActiveVideo = true;
        }
      });
    },

    // Tooltip methods for camera movement cards
    showMovementTooltip(movementValue, event) {
      const cardRect = event.currentTarget.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const estimatedTooltipWidth = 350; // Max width of tooltip
      const estimatedTooltipHeight = 400; // Estimated height including animation

      // Calculate horizontal positioning
      let leftPosition = cardRect.right + 16; // Default: show to the right with 16px gap

      // If tooltip would overflow right edge, show to the left
      if (leftPosition + estimatedTooltipWidth > windowWidth - 20) {
        leftPosition = cardRect.left - estimatedTooltipWidth - 16;
      }

      // If still overflowing left edge, center it
      if (leftPosition < 20) {
        leftPosition = Math.max(20, (windowWidth - estimatedTooltipWidth) / 2);
      }

      // Calculate vertical positioning - center tooltip relative to the card
      let topPosition = cardRect.top + cardRect.height / 2 - estimatedTooltipHeight / 2;

      // Ensure tooltip doesn't go off screen
      if (topPosition < 20) {
        topPosition = 20; // Minimum padding from top
      } else if (topPosition + estimatedTooltipHeight > windowHeight - 20) {
        topPosition = windowHeight - estimatedTooltipHeight - 20; // Minimum padding from bottom
      }

      this.tooltipStyles = {
        top: `${topPosition}px`,
        left: `${leftPosition}px`
      };

      this.hoveredMovement = movementValue;
    },

    hideMovementTooltip() {
      this.hoveredMovement = null;
    },

    getMovementDescription(movementValue) {
      const descriptions = {
        'Turntable 360': 'Creates a smooth 360-degree rotation around your subject, perfect for showcasing products or creating dynamic reveals.',
        'Hero Focus': 'Maintains focus on the main subject while creating subtle camera movement that draws attention to key details.',
        'Smooth Entry': 'Generates an elegant camera approach that gradually reveals your subject with cinematic smoothness.',
        'Gimbal Glide': 'Mimics professional gimbal movement with fluid, stabilized motion that adds production value to any shot.',
        'Portrait Pan': 'Specially designed for portraits and people, creating gentle camera movement that enhances facial features.',
        'Real Look': 'Produces natural, realistic camera movement that feels authentic and unobtrusive to the viewer.'
      };
      return descriptions[movementValue] || '';
    },

    async downloadVideo(videoUrl) {
      try {
        this.$q.notify({
          type: 'info',
          message: 'Preparing video for download...',
          position: 'top',
          timeout: 2000
        });

        // Fetch the video file as a blob
        const response = await fetch(videoUrl);
        if (!response.ok) throw new Error('Failed to fetch video');

        const blob = await response.blob();

        // Create a blob URL for the video
        const blobUrl = URL.createObjectURL(blob);

        // Create and trigger download link
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `video-${this.selectedLabel.replace(/\s+/g, '-').toLowerCase()}.mp4`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);

        this.$q.notify({
          type: 'positive',
          message: 'Video downloaded successfully',
          position: 'top',
          timeout: 2000
        });
      } catch (error) {
        console.error('Download failed:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to download video',
          position: 'top',
          timeout: 2000
        });
      }
    },

    async generateVideo() {
      if (!this.selectedLabel) {
        this.$q.notify({
          color: 'negative',
          textColor: 'white',
          icon: 'warning',
          message: 'Please select a camera movement type'
        });
        return;
      }

      this.isGenerating = true;
      this.operationStatus = 'starting';
      this.operationMessage = 'Preparing image for video generation...';

      // Get the image from props
      const imageData = this.currentImage;
      if (!imageData) {
        this.$q.notify({
          message: 'Invalid image data. Please try again.',
          color: 'negative',
          position: 'top'
        });
        this.isGenerating = false;
        this.isCompletingProgress = false;
        this.pendingVideoUrl = null;
        return;
      }

      // Prepare request parameters for Kling API
      const params = {
        label: this.selectedLabel,
        content_id: imageData.id || imageData.content_id // Use real content ID from uploaded or gallery image
      };

      this.operationMessage = 'Preparing image...';

      try {
        // Handle uploaded images (from drag & drop)
        if (imageData.file) {
          // For uploaded images, use the file directly
          this.operationMessage = 'Generating video with AI...';
          params.image = imageData.file;
          this.sendGenerateRequest(params);
        }
        // Handle gallery images (from existing gallery)
        else if (imageData.optimized_url || imageData.url) {
          const imageUrl = imageData.optimized_url || imageData.url;

          // Convert image URL to blob for upload (using axios like other tools)
          const response = await axios.get(imageUrl, {
            responseType: 'blob'
          });
          const imageBlob = response.data;

          this.operationMessage = 'Generating video with AI...';
          params.image = new Blob([imageBlob], { type: 'image/webp' });
          this.sendGenerateRequest(params);
        } else {
          throw new Error('No valid image source found');
        }
      } catch (error) {
        console.error('Error processing image:', error);

        this.$q.notify({
          message: 'Failed to process the image. Please try again.',
          color: 'negative',
          position: 'top'
        });
        this.isGenerating = false;
        this.isCompletingProgress = false;
        this.pendingVideoUrl = null;
      }
    },

    sendGenerateRequest(params) {
      this.operationStatus = 'processing';
      this.operationMessage = 'Generating video with AI...';

      // Call the API to generate the video using Kling
      ReplicateApi.generateVideoWithKling(params)
        .then(response => {
          console.log('API Response:', response);
          // Extract prediction_id from the nested data structure
          const predictionId = response.data?.prediction_id;

          if (!predictionId) {
            console.error('Missing prediction_id in response:', response);
            throw new Error('No prediction ID returned');
          }

          console.log('Using prediction ID:', predictionId);

          // Set current tracking ID
          this.currentPredictionId = predictionId;

          // Start the progress timer only after we have the real prediction ID
          this.startProgressTimer();

          // Show the progress dialog with real prediction ID
          this.$store.dispatch('videoGenerationProgress/startVideoGeneration', {
            predictionId: predictionId,
            imageUrl: this.currentImage.optimized_url || this.currentImage.url,
            cameraEffect: this.selectedLabel,
            fileName: `AI Video - ${this.selectedLabel}`
          });

          // Start polling for the prediction status
          this.pollPredictionStatus(predictionId);
        })
        .catch(error => {
          console.error('Error generating video:', error);

          this.$q.notify({
            message: 'Failed to generate video. Please try again.',
            color: 'negative',
            position: 'top'
          });
          this.isGenerating = false;
          this.operationStatus = 'error';
          this.operationMessage = 'An error occurred. Please try again.';

          // Use centralized failure handler if we have a prediction ID
          if (this.currentPredictionId) {
            this.$store.dispatch('videoGenerationProgress/failGeneration', {
              id: this.currentPredictionId,
              message: 'An error occurred. Please try again.'
            });
          }

          this.isCompletingProgress = false;
          this.pendingVideoUrl = null;
          this.currentPredictionId = null;
        });
    },

    pollPredictionStatus(predictionId) {
      const maxRetries = 60; // Maximum number of retries (5 minutes if checking every 5 seconds)
      let retryCount = 0;

      const checkStatus = () => {
        if (retryCount >= maxRetries) {
          this.operationStatus = 'error';
          this.operationMessage = 'Generation timed out. Please try again.';

          // Use centralized failure handler
          if (this.currentPredictionId) {
            this.$store.dispatch('videoGenerationProgress/failGeneration', {
              id: this.currentPredictionId,
              message: 'Generation timed out. Please try again.'
            });
          }

          this.isGenerating = false;
          this.stopProgressTimer(); // Clean up progress timer on timeout
          this.isCompletingProgress = false;
          this.currentPredictionId = null;
          return;
        }

        // Use getPredictionStatus instead of checkPrediction and pass the image ID
        ReplicateApi.getPredictionStatus(predictionId)
          .then(response => {
            console.log('Check prediction response:', response);
            // Handle response format from getPredictionStatus
            const { prediction } = response.data;
            const status = prediction?.status;
            const output = prediction?.output;

            if (status === 'succeeded' && output) {
              // Prediction completed successfully
              const videoUrl = output;

              // Store the video URL but don't display it yet
              this.pendingVideoUrl = videoUrl;

              // Update status to show completion during progress animation
              this.operationStatus = 'success';
              this.operationMessage = 'Video generated successfully!';

              // Use the centralized completion handler (which will handle auto-upload)
              this.$store.dispatch('videoGenerationProgress/handleGenerationCompletion', {
                id: this.currentPredictionId,
                videoUrl: videoUrl,
                message: 'Video generated successfully!',
                isActivelyManaged: false // Let the centralized handler manage auto-upload
              });

              // Reload video history to get the latest completed generation
              this.reloadVideoHistory();

              // Complete the progress indicator with callback to display video
              this.completeProgress(() => {
                // After progress completion, display the video but keep the state stable
                this.displayCompletedVideo();
                // Clear prediction ID after completion
                this.currentPredictionId = null;
              });
            } else if (status === 'failed') {
              // Prediction failed
              this.operationStatus = 'error';
              this.operationMessage = 'Generation failed. Please try again.';

              // Use the centralized failure handler
              this.$store.dispatch('videoGenerationProgress/failGeneration', {
                id: this.currentPredictionId,
                message: 'Generation failed. Please try again.'
              });

              this.isGenerating = false;
              this.stopProgressTimer(); // Clean up progress timer on failure
              this.isCompletingProgress = false;
              // Clear prediction ID on failure
              this.currentPredictionId = null;
            } else if (status === 'processing' || status === 'starting') {
              // Still processing, continue polling
              this.operationStatus = status;
              this.operationMessage = status === 'processing' ? 'Generating video... This may take a minute' : 'Starting video generation';

              retryCount++;
              setTimeout(checkStatus, 5000); // Check again in 5 seconds
            } else {
              // Unknown status, keep polling
              retryCount++;
              setTimeout(checkStatus, 5000);
            }
          })
          .catch(error => {
            console.error('Error checking prediction status:', error);
            retryCount++;

            // If we've exceeded max retries due to errors, stop progress timer
            if (retryCount >= maxRetries) {
              this.stopProgressTimer();
              this.isGenerating = false;
              this.operationStatus = 'error';
              this.operationMessage = 'Connection error. Please try again.';

              // Use centralized failure handler
              if (this.currentPredictionId) {
                this.$store.dispatch('videoGenerationProgress/failGeneration', {
                  id: this.currentPredictionId,
                  message: 'Connection error. Please try again.'
                });
              }

              this.isCompletingProgress = false;
              this.currentPredictionId = null;
              return;
            }

            // Continue polling despite error
            setTimeout(checkStatus, 5000);
          });
      };

      // Start polling
      checkStatus();
    },

    // Load video history from API (one-time call)
    async loadVideoHistoryFromApi() {
      try {
        if (!this.currentImage || !this.currentImage.id) {
          console.warn('No current image or image ID available for loading video history');
          return;
        }

        const contentId = this.currentImage.id || this.currentImage.content_id;
        const contentType = 'image'; // Assuming we're working with images for video generation

        // Load the video history
        const historyData = await this.$store.dispatch('videoHistory/loadVideoHistory', {
          contentId,
          contentType
        });

        console.log('Video history loaded for content:', contentId, historyData);

        // For any processing items, add them to GlobalVideoGenerationProgress
        if (historyData && historyData.histories) {
          const processingItems = historyData.histories.filter(history => history.job_status === 'processing' || history.job_status === 'starting');

          for (const item of processingItems) {
            // Calculate elapsed time from creation time to preserve timing
            const startTimeMs = new Date(item.created_at).getTime();
            const elapsed = Date.now() - startTimeMs;
            const progressDuration = 240000; // 4 minutes in milliseconds

            // Calculate progress based on elapsed time (same logic as GlobalVideoGenerationProgress)
            let calculatedProgress = (elapsed / progressDuration) * 100;
            if (calculatedProgress >= 95 && item.job_status === 'processing') {
              // Slow down progress significantly after 95% to avoid reaching 100% while still generating
              const overtime = elapsed - progressDuration * 0.95;
              const slowProgress = 95 + (overtime / (progressDuration * 0.5)) * 4; // Very slow progress from 95% to 99%
              calculatedProgress = Math.min(slowProgress, 99);
            } else {
              calculatedProgress = Math.min(calculatedProgress, 95);
            }

            // Add to global progress tracking with preserved timing using addGeneration directly
            const generationData = {
              id: item.job_id, // Use job_id as the generation ID
              predictionId: item.job_id,
              imageUrl: this.currentImage.optimized_url || this.currentImage.url,
              cameraEffect: item.camera_effect || 'AI Generated',
              fileName: `AI Video - ${item.camera_effect || 'Generated'}`,
              progress: Math.max(0, calculatedProgress), // Ensure progress is never negative
              status: item.job_status,
              startTime: item.created_at, // ISO string
              progressStartTime: startTimeMs, // Milliseconds for calculations
              message: item.job_status === 'processing' ? 'Generating video...' : 'Starting generation...',
              videoUrl: item.result || null,
              isRestored: true // Mark as restored to avoid auto-showing dialog
            };

            this.$store.dispatch('videoGenerationProgress/addGeneration', generationData);
          }

          // If we added processing items, ensure timers are running and show the dialog
          if (processingItems.length > 0) {
            this.$store.dispatch('videoGenerationProgress/ensureProgressTimer');
            // Show the GlobalVideoGenerationProgress dialog for persistent tracking
            this.$store.dispatch('videoGenerationProgress/showDialog');
          }
        }
      } catch (error) {
        console.error('Error loading video history from API:', error);
      }
    },

    // Reload video history after new video generation completes
    async reloadVideoHistory() {
      try {
        if (!this.currentImage || !this.currentImage.id) {
          return;
        }

        const contentId = this.currentImage.id || this.currentImage.content_id;
        const contentType = 'image';

        // Reload the video history to get the latest data
        await this.$store.dispatch('videoHistory/loadVideoHistory', {
          contentId,
          contentType
        });

        console.log('Video history reloaded');
      } catch (error) {
        console.error('Error reloading video history:', error);
      }
    },

    // Use shared time formatting utility from helpers
    getFormattedTimeRemaining(generation) {
      return helpers.getFormattedTimeRemaining(generation);
    },

    handleVideoError(event, item) {
      console.error('Video failed to load:', item.url, event);
      // Mark this video as having an error
      this.$set(item, 'hasError', true);
    },

    handleVideoLoaded(_, item) {
      // Mark this video as successfully loaded
      this.$set(item, 'hasError', false);
    },

    playVideo(item, event) {
      // Don't play if video has an error
      if (item.hasError) {
        this.$q.notify({
          type: 'negative',
          message: 'This video failed to load and cannot be played',
          position: 'top',
          timeout: 2000
        });
        return;
      }

      // If this was triggered by a button click, find the parent video element
      const videoElement = event && event.target ? event.target.closest('.video-item').querySelector('video') : null;

      if (videoElement) {
        // Pause the main video player if it's playing
        if (this.$refs.videoPlayer && !this.$refs.videoPlayer.paused) {
          this.$refs.videoPlayer.pause();
        }

        // Play the history video directly in its own player
        if (videoElement.paused) {
          videoElement.play().catch(error => {
            console.error(`Error playing history video for ${item.label}:`, error);
            this.$set(item, 'hasError', true);
          });
        } else {
          videoElement.pause();
        }
      }
    },

    // Handle image removal from upload area
    handleImageRemoved() {
      if (this.uploadedImage && this.uploadedImage.url) {
        URL.revokeObjectURL(this.uploadedImage.url);
      }
      this.uploadedImage = null;
    },

    // New methods for handling image upload
    onDragOver(event) {
      event.preventDefault();
      this.isDragging = true;
    },
    onDragLeave(event) {
      event.preventDefault();
      this.isDragging = false;
    },
    onDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
      event.target.value = '';
    },
    processFile(file) {
      // Validate file type
      const supportedTypes = ['image/jpeg', 'image/jpg', 'image/webp'];
      if (!supportedTypes.includes(file.type)) {
        this.$q.notify({
          type: 'negative',
          message: 'Please select a valid image file (JPEG or WebP)',
          position: 'top',
          timeout: 3000
        });
        return;
      }

      // Validate file size (max 50MB)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        this.$q.notify({
          type: 'negative',
          message: 'File size must be less than 50MB',
          position: 'top',
          timeout: 3000
        });
        return;
      }

      // Create image preview without uploading yet
      const imageUrl = URL.createObjectURL(file);
      this.uploadedImage = {
        file: file, // Store the file for later upload
        url: imageUrl,
        optimized_url: imageUrl,
        preview_url: imageUrl,
        name: file.name,
        size: file.size,
        type: file.type,
        isUploaded: false // Flag to indicate this needs to be uploaded
      };
    },

    // Method to handle the actual upload when user clicks upload button
    async uploadSelectedImage() {
      if (!this.uploadedImage || !this.uploadedImage.file) {
        return;
      }

      let globalUpload = null;

      try {
        this.$q.notify({
          type: 'info',
          message: 'Uploading image...',
          position: 'top',
          timeout: 2000
        });

        // Add upload to GlobalUploadProgress tracking
        const globalUploads = await this.$store.dispatch('uploadProgress/addUploads', [this.uploadedImage.file]);
        globalUpload = globalUploads[0];

        // Show GlobalUploadProgress dialog
        this.$store.dispatch('uploadProgress/showDialog');

        // Update progress for upload preparation phase
        await this.$store.dispatch('uploadProgress/updateUpload', {
          id: globalUpload.id,
          data: {
            status: 'uploading'
          }
        });

        // Upload the image using the fileUpload utility
        const uploadedContent = await fileUpload.uploadFile(this.uploadedImage.file, createProgressCallback(this.$store, globalUpload.id), globalUpload.id);

        if (uploadedContent) {
          // Update global progress with real content ID
          await this.$store.dispatch('uploadProgress/updateUploadWithRealId', {
            tempId: globalUpload.id,
            realId: uploadedContent.id,
            data: {
              canDelete: true,
              contentType: uploadedContent.content_type || 'image'
            }
          });

          // Clean up the preview URL
          if (this.uploadedImage.url) {
            URL.revokeObjectURL(this.uploadedImage.url);
          }

          // Update with real uploaded content data
          this.uploadedImage = {
            id: uploadedContent.id,
            content_id: uploadedContent.id,
            url: uploadedContent.url,
            optimized_url: uploadedContent.optimized_url || uploadedContent.url,
            preview_url: uploadedContent.preview_url,
            name: uploadedContent.name || this.uploadedImage.name,
            size: uploadedContent.size || this.uploadedImage.size,
            type: uploadedContent.type || this.uploadedImage.type,
            isUploaded: true // Flag to indicate this was uploaded
          };

          this.$q.notify({
            type: 'positive',
            message: 'Image uploaded successfully! You can now generate videos.',
            position: 'top',
            timeout: 2000
          });

          // Invalidate gallery queries to refresh the content automatically
          try {
            if (this.queryClient) {
              // Invalidate all content queries to ensure gallery refreshes
              await this.queryClient.invalidateQueries({
                queryKey: ['content'],
                exact: false // This will match all queries starting with ['content']
              });
              await this.queryClient.invalidateQueries({ queryKey: ['collections'] });

              console.log('Gallery queries invalidated after image upload');
            } else {
              console.warn('QueryClient not available for invalidation');
            }
          } catch (queryError) {
            console.warn('Failed to invalidate gallery queries:', queryError);
          }

          // Dispatch global event that ImagesGrid can listen to for gallery refresh
          if (typeof window !== 'undefined' && window.dispatchEvent) {
            window.dispatchEvent(
              new CustomEvent('content-uploaded', {
                detail: { contentId: uploadedContent.id, contentType: 'image' }
              })
            );
            console.log('Dispatched content-uploaded event for image:', uploadedContent.id);
          }

          // Load video history for the newly uploaded content
          this.loadVideoHistoryFromApi();
        } else {
          throw new Error('Upload failed - no content returned');
        }
      } catch (error) {
        console.error('Error uploading image:', error);

        // Update global progress with error state
        if (globalUpload) {
          await this.$store.dispatch('uploadProgress/updateUpload', {
            id: globalUpload.id,
            data: {
              progress: 100,
              status: 'error',
              message: error.message || 'Failed to upload image'
            }
          });
        }

        this.$q.notify({
          type: 'negative',
          message: 'Failed to upload image. Please try again.',
          position: 'top',
          timeout: 3000
        });
      }
    },

    handleImageLoad() {
      // Handle image load event
    },
    handleImageError() {
      // Handle image error event
    },
    getImageName() {
      if (this.uploadedImage && this.uploadedImage.name) {
        return this.uploadedImage.name;
      }
      if (this.image && this.image.name) {
        return this.image.name;
      }
      if (this.image && this.image.url) {
        // Extract filename from URL
        const url = this.image.url;
        const filename = url.substring(url.lastIndexOf('/') + 1);
        return filename || 'Selected Image';
      }
      return 'Selected Image';
    },
    getImageSize() {
      if (this.uploadedImage && this.uploadedImage.size) {
        return this.formatFileSize(this.uploadedImage.size);
      }
      if (this.image && this.image.size) {
        return this.formatFileSize(this.image.size);
      }
      return null;
    },
    formatFileSize(bytes) {
      if (!bytes) return null;

      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }
  }
};
</script>

<style lang="scss" scoped>
.video-generator-dialog {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.generator-container {
  background: var(--main-bg-color);
  height: 100vh;
  display: grid;
  width: 100%;
  grid-template-columns: 350px 1fr;
  gap: 16px;
  overflow: hidden;
  position: relative;
  padding: 16px 16px 16px 16px;
}

/* Left Section - Image Preview and Controls */
.left-section {
  background: var(--secondary-bg-color);
  padding: 16px;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  box-shadow: 1px 1px 10px var(--card-shadow-color);
}

.section-header {
  margin: 8px 0 16px 0;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  padding: 0;
}

.section-title {
  @include mona-sans-font($size: 22px, $bolded: true, $color: var(--main-text-color), $weight: 700);
  margin: 0 0 6px 0;
  line-height: 1.4;
}

.section-description {
  @include inter-font($size: 14px, $bolded: false, $color: var(--secondary-text-color), $weight: 400);
  margin: 0;
  line-height: 1.4;
}

.upload-section {
  width: 100%;
  margin-bottom: 16px;
}

.upload-area {
  border: 2px dashed var(--grey-hr);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: var(--secondary-bg-color);
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    border-color: var(--border-outlined-btn-color);
    background-color: rgba(254, 203, 47, 0.02);
  }

  &.drag-over {
    border-color: var(--border-outlined-btn-color);
    background-color: rgba(254, 203, 47, 0.05);
    transform: scale(1.02);
  }
}

.hidden-input {
  display: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.upload-icon-container {
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 50%;
  background-color: var(--main-bg-color);
}

.upload-text {
  margin-bottom: 16px;
  text-align: center;
}

.upload-main-text {
  @include mona-sans-font($size: 18px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.upload-sub-text {
  @include inter-font($size: 14px, $color: var(--secondary-text-color), $weight: 400);
  margin: 0;
  line-height: 1.4;
}

.upload-formats {
  @include inter-font($size: 12px, $color: var(--secondary-text-color), $weight: 400);
  background-color: var(--main-bg-color);
  padding: 6px 12px;
  border-radius: 16px;
  display: inline-block;
}

.image-preview-section {
  width: 100%;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  border: 1px solid var(--grey-hr);
}

.selected-image-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.image-preview-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
}

.selected-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 8px;
  border-radius: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;

  .image-preview-wrapper:hover & {
    opacity: 1;
  }
}

.remove-btn {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 0;
  width: 32px;
  height: 32px;
  min-height: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(244, 67, 54, 0.9);
    color: white;
  }

  :deep(.q-icon) {
    font-size: 16px;
  }
}

.image-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0 0 8px 8px;
  color: white;
}

.image-name {
  @include inter-font($size: 12px, $bolded: true, $color: white, $weight: 500);
  margin: 0;
}

.image-size {
  @include inter-font($size: 10px, $bolded: false, $color: white, $weight: 400);
  margin-top: 4px;
}

.upload-action-section {
  margin-top: 12px;
}

.upload-action-button {
  width: 100%;
  height: 40px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
}

/* Camera Movement Section */
.camera-movement-section {
  margin-bottom: 16px;
}

.movement-header {
  margin-bottom: 16px;
}

.movement-caption {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  margin-bottom: 4px;
  display: block;
}

.movement-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.movement-card {
  background: var(--main-bg-color);
  border: 2px solid var(--grey-hr);
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  text-align: center;

  &:hover:not(.movement-card--disabled) {
    border-color: var(--border-outlined-btn-color);
    background-color: rgba(254, 203, 47, 0.05);
    transform: translateY(-2px);
  }

  &--selected {
    border-color: var(--border-outlined-btn-color);
    background-color: rgba(254, 203, 47, 0.1);

    .movement-icon {
      color: var(--border-outlined-btn-color);
    }
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.movement-icon {
  margin-bottom: 8px;
  color: var(--secondary-text-color);
  transition: color 0.3s ease;
}

.movement-label {
  @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  line-height: 1.2;
}

.action-section {
  margin-top: 8px;
}

.action-button {
  width: 100%;
  height: 50px;
  padding: 12px 16px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  @include inter-font($size: 15px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  display: flex;
  align-items: center;
  justify-content: center;

  &--primary {
    background: var(--main-btn-color);
    color: var(--main-text-color);

    &:hover {
      background: var(--main-btn-color);
      opacity: 0.9;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.button-icon {
  margin-right: 8px;
  font-size: 16px;
}

.status-container {
  padding: 16px;
  background: var(--main-bg-color);
  border-radius: 8px;
}

/* Right Section - Generated Videos Grid */
.right-section {
  padding: 0 16px 16px 16px;
  background: var(--main-bg-color);
  border-radius: 10px;
}

.section-header-generator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.badge {
  background: var(--main-bg-color);
  color: var(--secondary-text-color);
  padding: 4px 12px;
  border-radius: 16px;
  @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color), $weight: 500);
}

.videos-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 10px;
  height: calc(100vh - 200px);
  overflow-y: auto;
  align-content: start;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
}

.video-placeholder {
  border: 1px solid var(--grey-hr);
  border-radius: 8px;
  background: var(--secondary-bg-color);
  position: relative;
  height: 230px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.create-placeholder {
    border: 2px dashed var(--grey-hr);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--border-outlined-btn-color);
      background-color: rgba(254, 203, 47, 0.02);
    }
  }
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;

  .placeholder-icon-container {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--main-bg-color);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;
  }

  .placeholder-text {
    @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color), $weight: 400);
  }
}

.video-item {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--card-shadow-color);
  background: var(--secondary-bg-color);
  height: 230px;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease;

  &.active {
    box-shadow: 0 0 0 2px var(--main-btn-color);
  }

  &.processing-item {
    height: 230px;
  }

  .video-thumbnail-container {
    position: relative;
    width: 100%;
    aspect-ratio: 16/9;
    overflow: hidden;
    background: var(--main-bg-color);

    &.processing-thumbnail {
      background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
      border: 2px dashed #ddd;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    }
  }

  .video-thumbnail {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;

    .error-text {
      font-size: 12px;
      margin-top: 8px;
      text-align: center;
    }
  }

  .duration {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .video-info {
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .video-details {
    flex: 1;
    overflow: hidden;
  }

  .video-title {
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 500);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .video-type {
    @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color), $weight: 400);
    margin-top: 4px;
  }

  .video-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.processing-item {
  opacity: 0.9;
}

.processing-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  width: 90%;
  max-width: 200px;
}

.progress-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-circle {
  @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color), $weight: 600);
}

.progress-percentage {
  @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color), $weight: 600);
}

.processing-text {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 500);
}

.time-remaining {
  @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color), $weight: 400);
}

.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  .processing-indicator {
    position: static;
    transform: none;
    gap: 4px;
    max-width: none;
    width: auto;
  }

  .progress-percentage-small {
    font-size: 10px;
    font-weight: 600;
  }

  .processing-text-small {
    font-size: 11px;
    color: var(--secondary-text-color);
    text-align: center;
  }

  .time-remaining-small {
    font-size: 10px;
    color: var(--main-btn-color);
    text-align: center;
  }
}

.video-placeholder-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--main-bg-color);
  gap: 8px;

  .placeholder-text {
    font-size: 12px;
    color: var(--secondary-text-color);
  }
}

.empty-state {
  border: 2px dashed #e0e0e0;
  background: #fafafa;
}

.empty-state:hover {
  border-color: #1976d2;
  background: #f3f8ff;
}

/* Helper Text Styling */
.helper-text-container {
  margin: 12px 0;
}

.helper-text {
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid;
  transition: all 0.2s ease;

  &--warning {
    background-color: rgba(#f39c12, 0.08);
    border-color: rgba(#f39c12, 0.2);

    .helper-icon {
      color: var(--warning);
    }

    .helper-message {
      color: var(--warning);
    }
  }

  &--success {
    background-color: rgba(#2ecc71, 0.08);
    border-color: rgba(#2ecc71, 0.2);

    .helper-icon {
      color: var(--positive);
    }

    .helper-message {
      color: var(--positive);
    }
  }

  &--info {
    background-color: rgba(#3498db, 0.08);
    border-color: rgba(#3498db, 0.2);

    .helper-icon {
      color: #3498db;
    }

    .helper-message {
      color: #3498db;
    }
  }
}

.helper-text-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.helper-icon {
  flex-shrink: 0;
  font-size: 18px;
}

.helper-message {
  @include inter-font($size: 14px, $bolded: false, $weight: 500);
  line-height: 1.4;
  margin: 0;
}
</style>
